"use client"

/**
 * Reusable `DataTable` / `HubTable` cell primitives — extracted from
 * `columns-showcase.tsx` so every hub composes its grid from the same set of
 * named, accessible, copy-paste-free renderers.
 *
 * **Why this module exists.** Without a shared home, each hub would re-derive
 * progress bars, currency formatting, rating stars, attachment chips, relative
 * times, etc. — drifting in spacing, color, and a11y treatment. These cells
 * pair color + glyph (WCAG 1.4.1), keep tabular numbers right-aligned, and
 * expose a focusable `Tip` for any glyph-only signal.
 *
 * **Composition only.** Every renderer is a pure composition of existing
 * primitives (`@/components/ui/*`, `@/components/list-hub-status-badge`,
 * `Intl` formatters, Font Awesome icon classes). No new design tokens, no new
 * package surface — drop these into any `ColumnDef<TRow>['cell']`.
 *
 * **Live catalog:** `apps/web/components/columns-showcase.tsx` (hosted at
 * `/columns`) renders every export below as its own column so designers,
 * engineers, and AI agents can see the cell in situ before picking it.
 *
 * **Skill reference:** `.cursor/skills/exxat-token-economy/SKILL.md` §3 names
 * each export below in its "primitive aliases" table so the AI imports
 * directly instead of re-implementing.
 */

import * as React from "react"
import { AvatarGroup, AvatarGroupCount, AvatarInitials } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FAVORITE_HOVER_GROUP, FavoriteToggleButton } from "@/components/ui/favorite-toggle-button"
import { Tip } from "@/components/ui/tip"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import { relativeTime } from "@/lib/relative-time"
import {
  formatDateUS,
  parseRowDateToYmd,
  ymdToLocalDate,
} from "@/lib/date-filter"
import { formatImportDateParts } from "@/components/assessment-structure/import-source-picker-shared"
import { cn } from "@/lib/utils"

export { FAVORITE_HOVER_GROUP }

/**
 * Hover-reveal class for row overflow / secondary actions. Pair with
 * `group/row` on the table row (HubTable / DataTable already sets this).
 */
export const ROW_ACTION_REVEAL =
  "opacity-0 transition-opacity duration-150 group-hover/row:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"

/* ────────────────────────────────────────────────────────────────────────── *
 * Shared helpers
 * ────────────────────────────────────────────────────────────────────────── */

/** Missing-value cell. Renders the word, not a glyph, so sighted and
 *  screen-reader users read the same thing. Pass `label` when the column can
 *  name what is missing ("No date", "No files"). */
export function EmptyCell({ label = "None" }: { label?: string }) {
  return <span className="text-sm text-muted-foreground">{label}</span>
}

/** @deprecated Prefer `EmptyCell` — kept for hubs that still import the dash. */
export const EMPTY_DASH = (
  <span className="text-sm text-muted-foreground" aria-hidden="true">
    —
  </span>
)

/* ────────────────────────────────────────────────────────────────────────── *
 * Numeric / monetary
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Right-aligned plain numeric cell. Use for counts where the grid benefits
 * from column-aligned digits (attempts, downloads, file size N).
 */
export function NumericCell({
  value,
  fractionDigits = 0,
  className,
}: {
  value: number | null | undefined
  fractionDigits?: number
  className?: string
}) {
  if (value == null || Number.isNaN(value)) return <EmptyCell />
  return (
    <span className={cn("block text-right text-sm tabular-nums text-foreground", className)}>
      {Number(value).toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })}
    </span>
  )
}

/**
 * Currency cell — right-aligned, `tabular-nums`. `Intl.NumberFormat` honors
 * locale + currency; defaults to USD because the product is US-first.
 */
/* ────────────────────────────────────────────────────────────────────────── *
 * Currency
 * ────────────────────────────────────────────────────────────────────────── */

export function CurrencyCell({
  value,
  currency = "USD",
  locale = "en-US",
  maximumFractionDigits = 2,
}: {
  value: number | null | undefined
  currency?: string
  locale?: string
  maximumFractionDigits?: number
}) {
  const fmt = React.useMemo(
    () => new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits }),
    [locale, currency, maximumFractionDigits],
  )
  if (value == null || Number.isNaN(value)) return <EmptyCell label="No amount" />
  return (
    <span className="block text-right text-sm tabular-nums text-foreground">
      {fmt.format(value)}
    </span>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Progress + signal
 * ────────────────────────────────────────────────────────────────────────── */

export type ProgressTone = "auto" | "success" | "warning" | "danger" | "info"

/**
 * Progress bar — track + filled fill + numeric label. Auto-tones in thirds:
 * <34% destructive, <67% warning, ≥67% success. Pass an explicit `tone` to
 * override (e.g. "info" for non-judgmental quantity bars).
 */
export function ProgressCell({
  value,
  max = 100,
  tone = "auto",
  label,
  className,
}: {
  value: number | null | undefined
  max?: number
  tone?: ProgressTone
  /** Right-side label. Defaults to `${pct}%`. Pass `false` to hide. */
  label?: React.ReactNode | false
  className?: string
}) {
  if (value == null || Number.isNaN(value)) return <EmptyCell label="No progress" />
  const pct = Math.max(0, Math.min(100, Math.round((value / max) * 100)))
  const autoTone =
    pct < 34 ? "bg-destructive" :
    pct < 67 ? "bg-amber-500"   :
               "bg-emerald-500"
  const toneClass =
    tone === "success" ? "bg-emerald-500" :
    tone === "warning" ? "bg-amber-500"   :
    tone === "danger"  ? "bg-destructive" :
    tone === "info"    ? "bg-primary"     :
                         autoTone
  const labelNode =
    label === false ? null :
    label ?? <span className="text-xs tabular-nums text-muted-foreground">{pct}%</span>
  return (
    <div className={cn("flex min-w-[140px] max-w-[180px] flex-col gap-1.5", className)}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={`Progress ${pct} percent`}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn("h-full rounded-full transition-[width]", toneClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
      {labelNode}
    </div>
  )
}

export type SignalTone = "success" | "warning" | "danger" | "info" | "neutral"

/**
 * Three-bar signal indicator — same metaphor as Wi-Fi / cellular bars. Use
 * for ordinal scales (low/medium/high; easy/medium/hard). Color is *paired*
 * with bar count so the cell still communicates on monochrome + forced-colors.
 */
export function SignalBarsCell({
  level,
  max = 3,
  tone = "info",
  label,
}: {
  /** 1-indexed level. */
  level: number
  /** Total number of bars. Default 3. */
  max?: number
  tone?: SignalTone
  /** Accessible name; also used as the `Tip` content. */
  label: string
}) {
  const lvl = Math.max(0, Math.min(max, Math.round(level)))
  const toneClass =
    tone === "success" ? "bg-emerald-500" :
    tone === "warning" ? "bg-amber-500"   :
    tone === "danger"  ? "bg-destructive" :
    tone === "info"    ? "bg-primary"     :
                         "bg-foreground"
  return (
    <Tip side="top" label={label}>
      <span
        className="inline-flex items-end gap-0.5 cursor-default"
        role="img"
        aria-label={label}
        tabIndex={0}
      >
        {Array.from({ length: max }, (_, i) => {
          const bar = i + 1
          const filled = bar <= lvl
          // Stair-step the heights so the metaphor reads visually.
          const heightClass =
            bar === 1 ? "h-2" :
            bar === 2 ? "h-3" :
            bar === 3 ? "h-4" :
                        "h-5"
          return (
            <span
              key={bar}
              className={cn("w-1 rounded-sm", filled ? toneClass : "bg-muted", heightClass)}
              aria-hidden="true"
            />
          )
        })}
      </span>
    </Tip>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * People
 * ────────────────────────────────────────────────────────────────────────── */

export interface PersonStub {
  name: string
  initials: string
}

/**
 * Face rail — list of people with a `+N more` overflow chip. Each face gets a
 * `Tip` of the person's name; the overflow chip's tip lists the hidden names.
 * Uses non-overlapping avatars (gap, not negative margin) per Exxat DS rule.
 */
export function PeopleAvatarRailCell({
  people,
  visibleMax = 3,
  size = "sm",
  emptyLabel = "No people",
}: {
  people: PersonStub[] | undefined
  /** How many faces to show before `+N`. Default 3. */
  visibleMax?: number
  size?: "sm" | "md"
  emptyLabel?: string
}) {
  if (!people?.length) return <EmptyCell label={emptyLabel} />
  const visible = people.slice(0, visibleMax)
  const overflow = people.length - visible.length
  const sizeClass = size === "md" ? "size-7 text-xs" : "size-6 text-xs"
  return (
    <AvatarGroup data-size={size} className="gap-1">
      {visible.map((p) => (
        <Tip key={`${p.name}-${p.initials}`} side="top" label={p.name}>
          <AvatarInitials
            initials={p.initials}
            className={sizeClass}
            fallbackClassName="text-xs"
          />
        </Tip>
      ))}
      {overflow > 0 && (
        <Tip side="top" label={people.slice(visibleMax).map((p) => p.name).join(", ")}>
          <AvatarGroupCount
            tabIndex={0}
            aria-label={`${overflow} more${overflow === 1 ? "" : "s"}`}
            className={sizeClass}
          >
            +{overflow}
          </AvatarGroupCount>
        </Tip>
      )}
    </AvatarGroup>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * New / unread row affordance
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * One `text-sm` line-box tall gutter so a leading marker (e.g. `TableNewRowDot`)
 * centers on the title’s first line without `mt-*` nudges.
 */
export function TableRowMarkerGutter({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        "flex h-[calc(var(--text-sm)*var(--text-sm--line-height))] w-1.5 shrink-0 items-center",
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Subtle dot for unread/new table rows — pair with `row.isNew` + `bg-dt-new-row-bg`. */
export function TableNewRowDot({
  tone = "info",
  label = "New",
  className,
}: {
  tone?: "info" | "danger"
  /** Screen-reader text. `null` when the context already says "new". */
  label?: string | null
  className?: string
}) {
  return (
    <span className={cn("inline-flex shrink-0 items-center", className)}>
      <span
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          tone === "danger" ? "bg-destructive" : "bg-dt-new-row-dot",
        )}
        aria-hidden="true"
      />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Truncated text — Tip only when clipped
 * ────────────────────────────────────────────────────────────────────────── */

function useElementTruncated(
  ref: React.RefObject<HTMLElement | null>,
  syncKey: string
) {
  const [truncated, setTruncated] = React.useState(false)

  const measure = React.useCallback(() => {
    const el = ref.current
    if (!el) return
    setTruncated(
      el.scrollWidth > el.clientWidth + 1 ||
        el.scrollHeight > el.clientHeight + 1
    )
  }, [ref])

  React.useLayoutEffect(() => {
    measure()
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // Re-bind when `truncated` flips Tip on/off and remounts the node.
  }, [measure, ref, syncKey, truncated])

  return truncated
}

/**
 * Single-line (or line-clamped) copy that exposes a focusable `Tip` with the
 * full string **only when** CSS truncation / clamp is actually clipping.
 *
 * Use for table cells and dense list rows where `truncate` / `line-clamp-*`
 * would otherwise hide the end of the label (§8.6 — sighted users need a tip
 * when the glyph/text is incomplete).
 */
export function TruncatedText({
  text,
  className,
  side = "top",
  as: Comp = "span",
}: {
  text: string
  className?: string
  side?: "top" | "bottom" | "left" | "right"
  as?: "span" | "p"
}) {
  const ref = React.useRef<HTMLElement | null>(null)
  const truncated = useElementTruncated(ref, text)

  const node = (
    <Comp
      ref={ref as never}
      tabIndex={truncated ? 0 : undefined}
      className={cn("min-w-0", className)}
    >
      {text}
    </Comp>
  )

  if (!truncated || !text) return node

  return (
    <Tip label={text} side={side} triggerClassName="min-w-0 max-w-full">
      {node}
    </Tip>
  )
}

/**
 * Truncating interactive control (e.g. stem button) — tips `label` only when
 * the control's text is clipped. Child must accept a callback `ref`.
 */
export function TruncatedControl({
  label,
  children,
  side = "top",
}: {
  label: string
  children: (ref: React.RefCallback<HTMLElement>) => React.ReactElement
  side?: "top" | "bottom" | "left" | "right"
}) {
  const nodeRef = React.useRef<HTMLElement | null>(null)
  const truncated = useElementTruncated(nodeRef, label)
  const setRef = React.useCallback((node: HTMLElement | null) => {
    nodeRef.current = node
  }, [])

  const child = children(setRef)
  if (!truncated || !label) return child

  return (
    <Tip label={label} side={side} triggerClassName="min-w-0 max-w-full">
      {child}
    </Tip>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Name + favorite
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Name/title column with an inline favorite star — the "identity + favorite"
 * pattern used by every hub with a favoritable primary column (question
 * bank, course forms, course reports, …). One shared composition so hubs
 * don't each re-derive the hover-reveal star, gap, and text treatment
 * (`exxat-reuse-before-custom.mdc`). Star is trailing (after the text) —
 * keep new call sites consistent rather than flipping the icon to lead.
 */
export function FavoriteNameCell({
  label,
  isFavorite,
  onToggleFavorite,
  subtitle,
  interactive = false,
  badge,
}: {
  label: string
  isFavorite: boolean
  onToggleFavorite: () => void
  /** Secondary line under the label (mono ID, meta, etc.). */
  subtitle?: React.ReactNode
  /** Render `label` as a link-styled button (row opens its own detail view) instead of plain text. */
  interactive?: boolean
  /** Trailing badge/chip after the label (e.g. "New"). */
  badge?: React.ReactNode
}) {
  return (
    <div className={cn(FAVORITE_HOVER_GROUP, "flex min-w-0 items-start gap-2")}>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pe-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {interactive ? (
            <button
              type="button"
              className="min-w-0 break-words wrap-break-word truncate rounded-sm text-left text-sm font-medium text-interactive outline-none hover:text-interactive-hover-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            >
              {label}
            </button>
          ) : (
            <span className="line-clamp-2 min-w-0 break-words wrap-break-word text-sm font-medium text-foreground">{label}</span>
          )}
          {badge}
        </div>
        {subtitle}
      </div>
      <FavoriteToggleButton isFavorite={isFavorite} onToggle={onToggleFavorite} label={label} />
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Pills + chips
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Outlined pill with a leading FA icon — the "Type" pattern. Use for
 * single-select categorical fields where color isn't carrying meaning
 * (otherwise reach for `ListHubStatusBadge`).
 */
export function PillCell({
  label,
  icon,
  iconClassName,
  className,
}: {
  label: React.ReactNode
  /** FA glyph name without the family prefix, e.g. `"fa-list-check"`. */
  icon?: string
  iconClassName?: string
  className?: string
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "h-6 gap-1.5 border-border bg-background px-2 text-xs font-medium",
        className,
      )}
    >
      {icon ? (
        <i
          className={cn("fa-light text-xs text-muted-foreground", icon, iconClassName)}
          aria-hidden="true"
        />
      ) : null}
      <span className="text-foreground">{label}</span>
    </Badge>
  )
}

/**
 * Tag list with `+N` overflow. Use for free-form keyword tags (`#tag`). For
 * categorical pills, see `PillCell`; for status, see `ListHubStatusBadge`.
 */
export function TagListCell({
  tags,
  visibleMax = 2,
  formatLabel = (t) => `#${t}`,
}: {
  tags: string[] | undefined
  visibleMax?: number
  formatLabel?: (tag: string) => string
}) {
  if (!tags?.length) return <EmptyCell label="No tags" />
  const visible = tags.slice(0, visibleMax)
  const overflow = tags.length - visible.length
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((t) => (
        <Badge
          key={t}
          variant="secondary"
          className="h-5 px-1.5 text-xs font-medium leading-none"
        >
          {formatLabel(t)}
        </Badge>
      ))}
      {overflow > 0 && (
        <Tip side="top" label={tags.slice(visibleMax).map(formatLabel).join(", ")}>
          <span
            className="inline-flex h-5 cursor-default items-center justify-center rounded-md bg-muted px-1.5 text-xs font-medium leading-none text-muted-foreground"
            tabIndex={0}
            aria-label={`${overflow} more tag${overflow === 1 ? "" : "s"}`}
          >
            +{overflow}
          </span>
        </Tip>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Rating
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Star rating — N of `max` FA stars + numeric value. Color (amber) + glyph
 * change (solid vs. light) pair so the cell still reads on monochrome /
 * forced-colors modes (WCAG 1.4.1).
 */
export function RatingCell({
  value,
  max = 5,
  showValue = true,
}: {
  value: number | null | undefined
  max?: number
  showValue?: boolean
}) {
  if (value == null || Number.isNaN(value)) return <EmptyCell label="No rating" />
  const n = Math.max(0, Math.min(max, Math.round(value)))
  const label = `Rated ${n} of ${max}`
  return (
    <Tip side="top" label={label}>
      <span
        role="img"
        aria-label={label}
        tabIndex={0}
        className="inline-flex items-center gap-1 rounded-md cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <span className="inline-flex items-center gap-0.5" aria-hidden="true">
          {Array.from({ length: max }, (_, i) => {
            const filled = i < n
            return (
              <i
                key={i}
                className={cn(
                  filled ? "fa-solid text-amber-500" : "fa-light text-muted-foreground",
                  "fa-star text-xs",
                )}
              />
            )
          })}
        </span>
        {showValue ? (
          <span className="text-xs tabular-nums text-muted-foreground">{n}.0</span>
        ) : null}
      </span>
    </Tip>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Booleans
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Inline toggle — `ToggleSwitch` for a boolean lifecycle field (Published,
 * Active, Enabled). The callback receives the *next* checked state; the cell
 * stops row click propagation so toggling never opens the row.
 *
 * `ToggleSwitch` does not currently support a `disabled` state — if you need
 * to lock a row's toggle, render a static badge (`PillCell` with the current
 * state) instead.
 */
export function BooleanToggleCell({
  checked,
  onChange,
  labelOn = "On — click to turn off",
  labelOff = "Off — click to turn on",
}: {
  checked: boolean
  onChange: (next: boolean) => void
  labelOn?: string
  labelOff?: string
}) {
  return (
    <Tip side="top" label={checked ? labelOn : labelOff}>
      <span
        className="inline-flex items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <ToggleSwitch
          checked={checked}
          onChange={() => onChange(!checked)}
        />
      </span>
    </Tip>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Attachments / links / time
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Attachment indicator — paperclip + count chip; "No files" when zero. A
 * focusable `Tip` exposes the count for screen-reader users; the chip is
 * non-interactive — wire `onClick` from the column def if you need a popover.
 */
export function AttachmentCountCell({
  count,
}: {
  count: number | null | undefined
}) {
  if (!count) return <EmptyCell label="No files" />
  const labelText = `${count} attachment${count === 1 ? "" : "s"}`
  return (
    <Tip side="top" label={labelText}>
      <span
        className="inline-flex h-6 cursor-default items-center gap-1 rounded-md border border-border bg-background px-1.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        role="img"
        aria-label={labelText}
        tabIndex={0}
      >
        <i className="fa-light fa-paperclip text-xs text-muted-foreground" aria-hidden="true" />
        <span className="tabular-nums">{count}</span>
      </span>
    </Tip>
  )
}

/**
 * External link — truncated host label + `fa-arrow-up-right-from-square` mark.
 * Opens in a new tab with `noopener`; full URL surfaces in the `Tip`. The link
 * stops row click propagation so it never collides with the row's `onClick`.
 */
export function ExternalLinkCell({
  url,
  label,
  className,
}: {
  url: string | null | undefined
  /** Override the host-only label (e.g. "View source"). */
  label?: React.ReactNode
  className?: string
}) {
  if (!url) return <EmptyCell label="No link" />
  let host = url
  try {
    host = new URL(url).hostname.replace(/^www\./, "")
  } catch {
    /* keep the raw url */
  }
  return (
    <Tip side="top" label={url}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "inline-flex max-w-[180px] items-center gap-1 truncate rounded text-sm text-foreground transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          className,
        )}
      >
        <span className="truncate">{label ?? host}</span>
        <i className="fa-light fa-arrow-up-right-from-square text-xs text-muted-foreground" aria-hidden="true" />
      </a>
    </Tip>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Time
 * ────────────────────────────────────────────────────────────────────────── */

// The cascade lives in `lib/relative-time` so the question preview panel's
// version list and this cell cannot drift into two vocabularies for the same
// gap ("3 weeks ago" here, "21 days ago" there).

/**
 * Relative time — "3 hours ago" / "2 days ago" with a `Tip` exposing the
 * absolute timestamp on hover/focus. The visible label is the relative form
 * so scanning readers see recency at a glance.
 */
export function RelativeTimeCell({
  iso,
  now,
}: {
  iso: string | null | undefined
  /** Override "now" for deterministic snapshots. */
  now?: number
}) {
  if (!iso) return <EmptyCell label="No date" />
  const fmt = relativeTime(iso, now)
  if (!fmt) return <EmptyCell label="Invalid date" />
  return (
    <Tip side="top" label={fmt.absolute}>
      <span
        className="inline-block text-sm text-foreground/90 whitespace-nowrap rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        tabIndex={0}
      >
        {fmt.relative}
      </span>
    </Tip>
  )
}

/**
 * Hoisted: `new Intl.DateTimeFormat` compiles a locale pattern, and these
 * run once per cell per render. Built once, they are reused by every row.
 */
const MEDIUM_DATE = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" })
const SHORT_TIME = new Intl.DateTimeFormat(undefined, { timeStyle: "short" })

function dateTimeStackParts(input: {
  at?: number | null
  iso?: string | null
}): { date: string; time: string } | null {
  if (input.at != null) {
    const when = new Date(input.at)
    if (Number.isNaN(when.getTime())) return null
    return formatImportDateParts(input.at)
  }

  const raw = input.iso?.trim()
  if (!raw) return null

  const ymd = parseRowDateToYmd(raw)
  if (ymd) {
    const hasClock =
      raw.includes("T") ||
      /\d{1,2}:\d{2}/.test(raw) ||
      /\d{1,2}\s*(am|pm)/i.test(raw)
    if (!hasClock) {
      return { date: formatDateUS(ymd), time: "" }
    }
    const parsed = new Date(raw.includes("T") ? raw : ymdToLocalDate(ymd))
    if (Number.isNaN(parsed.getTime())) {
      return { date: formatDateUS(ymd), time: "" }
    }
    return {
      date: MEDIUM_DATE.format(parsed),
      time: SHORT_TIME.format(parsed),
    }
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return null
  return {
    date: MEDIUM_DATE.format(parsed),
    time: SHORT_TIME.format(parsed),
  }
}

/**
 * Absolute date with time on a muted second line — for import timestamps and
 * last-updated columns where the day is what you scan and the clock is detail.
 */
export function DateTimeStackCell({
  at,
  iso,
}: {
  /** Epoch ms — import timestamps, commit times. */
  at?: number | null
  /** ISO or row date string — library `updatedAt`. */
  iso?: string | null
}) {
  const parts = dateTimeStackParts({ at, iso })
  if (!parts) return <EmptyCell label="No date" />
  return (
    <div className="flex min-w-0 flex-col gap-0.5 whitespace-nowrap">
      <span className="text-sm tabular-nums text-foreground/90">
        {parts.date}
      </span>
      {parts.time ? (
        <span className="text-xs tabular-nums text-muted-foreground">
          {parts.time}
        </span>
      ) : null}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Row actions ⋯
 * ────────────────────────────────────────────────────────────────────────── */

export interface RowActionDef<TRow> {
  label: string
  /** FA glyph name without the family prefix, e.g. `"fa-pen-to-square"`. */
  icon: string
  onSelect: (row: TRow) => void
  /** Render as the destructive variant — separator + red label. */
  variant?: "destructive"
  /** Optional menu-item keyboard shortcut hint (e.g. `"⌘E"`). */
  shortcut?: string
  /** Disable the item without hiding it. */
  disabled?: boolean
}

/**
 * Row overflow `⋯` menu — generic across hubs. Pass the row and an array of
 * `{ label, icon, onSelect, variant?, shortcut? }`; destructive items
 * automatically gain a separator above. The trigger keeps an `aria-label` so
 * the button is named for screen readers.
 */
export function RowActionsCell<TRow>({
  row,
  actions,
  triggerLabel = "More options",
  align = "end",
}: {
  row: TRow
  actions: RowActionDef<TRow>[]
  /** Both the `Tip` content and the `aria-label` fallback. */
  triggerLabel?: string
  align?: "start" | "center" | "end"
}) {
  return (
    <DropdownMenu>
      <Tip side="top" label={triggerLabel} triggerClassName="inline-flex">
        <DropdownMenuTrigger asChild>
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={triggerLabel}
            className={cn(
              ROW_ACTION_REVEAL,
              // Table cells use max-w-0 + overflow-hidden; Button's default
              // outset ring-3 clips on all sides — paint the ring inside.
              "focus-visible:ring-inset",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fa-light fa-ellipsis text-sm" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
      </Tip>
      <DropdownMenuContent align={align}>
        {actions.map((a, i) => {
          const prev = actions[i - 1]
          const needsSeparator =
            a.variant === "destructive" && prev && prev.variant !== "destructive"
          return (
            <React.Fragment key={a.label}>
              {needsSeparator ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem
                onSelect={() => a.onSelect(row)}
                disabled={a.disabled}
                shortcut={a.shortcut}
                className={a.variant === "destructive" ? "text-destructive focus:text-destructive" : ""}
              >
                <i className={`fa-light ${a.icon}`} aria-hidden="true" />
                {a.label}
              </DropdownMenuItem>
            </React.Fragment>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}